/*
 * Copyright © HatioLab Inc. All rights reserved.
 */
import { Component, ComponentNature, RectPath, Shape, sceneComponent, RealObject, MaterialBank } from '@hatiolab/things-scene'
import type { State, SlotDef } from '@hatiolab/things-scene'
import { FloorBound, Mover, Placeable, type Alignment, type CarrierAttachPoint, type MoveOptions, type PlacementArchetype } from '@operato/scene-base'
import * as THREE from 'three'

/**
 * Cart — 전동 운반 카트 (powered transport cart).
 *
 * 개념상 사람이 운전하는 forklift 와 동류 = **위치-가변 FlowNode = Mover** (구분 기준은
 * 자율성·적재기구가 아니라 *위치 가변성*). 스스로 navigate(moveTo)하며 cargo 를 deck 에 싣고
 * 나른다. cargo 는 items(Holdable)로 보유, deck 캐파는 slot(Capacity)으로 바인딩.
 *
 * (구 host-following viz 오버레이 동작은 폐기 — powered cart 는 host 에 종속되지 않고 스스로 이동.
 *  "host 에 붙어 따라가는 시각 cart" 가 별도로 필요하면 별개 컴포넌트로 분리.)
 */
export interface CartState extends State {
  shelves?: number
  shelfColor?: string
  postColor?: string
  /** 이동 속도 (scene units/sec) — Mover.moveTo 가 소요시간 도출에 사용. */
  speed?: number
  /** deck 최대 적재 수 — canReceive 백프레셔의 하드 캡. default = shelves. */
  capacity?: number
}

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    { type: 'number', label: 'shelves', name: 'shelves', property: 'shelves' },
    { type: 'color', label: 'shelf-color', name: 'shelfColor', property: 'shelfColor' },
    { type: 'color', label: 'post-color', name: 'postColor', property: 'postColor' },
    { type: 'number', label: 'capacity', name: 'capacity' },
    { type: 'number', label: 'speed', name: 'speed', placeholder: 'units/sec' }
  ],
  help: 'scene/component/cart'
}

const DEFAULT_SHELF_COLOR = '#b0b0b0'
const DEFAULT_POST_COLOR = '#666666'

export class Cart3D extends RealObject {
  build() {
    super.build()

    var { width, height, depth, fillStyle, shelfColor, postColor, shelves = 2 } = this.component.state

    var w = width
    var h = height
    var d = depth || 60
    var numShelves = Math.max(1, Math.min(shelves, 5))

    var shelfThick = Math.max(1.5, d * 0.04)
    var postR = Math.min(w, h) * 0.03
    var totalH = d

    var shelfMat = MaterialBank.material('cart|shelf', { roughness: 0.4, metalness: 0.2 }, shelfColor || fillStyle || DEFAULT_SHELF_COLOR)
    var postMat = MaterialBank.material('cart|post', { roughness: 0.5, metalness: 0.3 }, postColor || DEFAULT_POST_COLOR)

    // ── 선반판 ──
    for (var i = 0; i < numShelves; i++) {
      var shelfY = (i / (numShelves - 1 || 1)) * (totalH - shelfThick)
      var shelfGeo = new THREE.BoxGeometry(w, shelfThick, h)
      var shelf = new THREE.Mesh(shelfGeo, shelfMat)
      shelf.position.y = shelfY + shelfThick / 2
      shelf.castShadow = true
      shelf.receiveShadow = true
      this.object3d.add(shelf)
    }

    // ── 기둥 4개 (모서리) ──
    var postGeo = new THREE.CylinderGeometry(postR, postR, totalH, 8)
    var insetX = w * 0.42
    var insetZ = h * 0.42

    for (var px of [-insetX, insetX]) {
      for (var pz of [-insetZ, insetZ]) {
        var post = new THREE.Mesh(postGeo, postMat)
        post.position.set(px, totalH / 2, pz)
        post.castShadow = true
        this.object3d.add(post)
      }
    }

    // 수직 중앙 정렬
    this.object3d.children.forEach(child => {
      child.position.y -= totalH / 2
    })
  }
}

/* ── 2D 렌더 ── */

function renderCart(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  fillStyle: string,
  shelfColor: string,
  postColor: string,
  shelves: number
) {
  var numShelves = Math.max(1, Math.min(shelves || 2, 5))
  var postW = Math.max(2, w * 0.06)
  var inset = postW / 2

  // ── 기둥 4개 ──
  ctx.fillStyle = postColor || DEFAULT_POST_COLOR
  for (var px of [x + inset, x + w - inset - postW]) {
    for (var py of [y + inset, y + h - inset - postW]) {
      ctx.fillRect(px, py, postW, postW)
    }
  }

  // ── 선반판 ──
  ctx.fillStyle = shelfColor || fillStyle || DEFAULT_SHELF_COLOR
  var shelfH = Math.max(2, h * 0.04)
  for (var i = 0; i < numShelves; i++) {
    var sy = y + (i / (numShelves - 1 || 1)) * (h - shelfH)
    ctx.fillRect(x, sy, w, shelfH)
  }

  // 외곽선
  ctx.strokeStyle = '#999999'
  ctx.lineWidth = 1
  ctx.strokeRect(x, y, w, h)
}

// Cart = 위치-가변 FlowNode = Mover (forklift/AGV/worker 와 동형, RectPath(Shape) 렌더).
//   Mover:      moveTo / pick / place — cargo 는 items(Holdable). deck 적재 캐파는 slots(Capacity).
//   FloorBound: 바닥 위 직립 유지.
// Container 아님 — cargo 는 items 이지 modeling 자식 아님. self-propelled 라 host-following 없음.
const CartBase = FloorBound(Mover(Placeable(RectPath(Shape)))) as unknown as typeof Component & {
  new (...args: any[]): Component & {
    isMover: boolean
    attachPointFor(carrier: Component): CarrierAttachPoint | undefined
    pick(carrier: Component, options?: MoveOptions): Promise<void>
    place(carrier: Component, holder: Component, options?: MoveOptions): Promise<void>
    pickAndPlace(carrier: Component, holder: Component, options?: MoveOptions): Promise<void>
  }
}

@sceneComponent('cart')
export class Cart extends CartBase {
  override get state(): CartState {
    return super.state as CartState
  }

  static placement: PlacementArchetype = 'floor'
  static align: Alignment = 'bottom'

  get nature() {
    return NATURE
  }

  get anchors() {
    return []
  }

  /**
   * deck 적재 slot (Capacity). 단일 slot 에 capacity 만큼 stack 적재 — sim 캐파 바인딩
   * (canReceive 백프레셔). 3D 적재 위치(stack 좌표)는 slot mount 방식으로 후속 (pallet 과 동일 보류).
   */
  get slots(): SlotDef[] {
    const cap = (this.state.capacity as number) ?? (this.state.shelves as number) ?? 2
    return [{ id: 'deck', maxCount: Math.max(1, cap), localPosition: { x: 0, y: 0, z: 0 }, arrangement: 'stack-y' }]
  }

  buildRealObject(): RealObject | undefined {
    return new Cart3D(this)
  }

  render(ctx: CanvasRenderingContext2D) {
    var { left, top, width, height } = this.bounds
    var fillStyle = this.getState('fillStyle')
    var shelfColor = this.getState('shelfColor')
    var postColor = this.getState('postColor')
    var shelves = this.getState('shelves')

    renderCart(ctx, left, top, width, height, fillStyle, shelfColor, postColor, shelves)

    ctx.beginPath()
    ctx.rect(left, top, width, height)
  }
}
